import { Component, forwardRef, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
    FormsModule,
    ReactiveFormsModule,
    NG_VALUE_ACCESSOR,
    ControlValueAccessor,
    FormBuilder,
    FormGroup,
    Validators,
} from '@angular/forms';
import { NgSelectModule } from '@ng-select/ng-select';
import { lastValueFrom } from 'rxjs';
import { CurrenciesService } from '../../services/currencies.service';
import { CurrencyEntryDto } from '../../../../core/data/new-dtos/insurance-type.dtos';
import { SharedModule } from '../../../../common/shared.module';

@Component({
    selector: 'app-currency-selector',
    standalone: true,
    imports: [CommonModule, FormsModule, ReactiveFormsModule, NgSelectModule, SharedModule],
    templateUrl: './currency-selector.component.html',
    styleUrls: ['./currency-selector.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => CurrencySelectorComponent),
            multi: true,
        },
    ],
})
export class CurrencySelectorComponent implements ControlValueAccessor, OnInit {
    currencies: CurrencyEntryDto[] = [];
    selectedIds: number[] = [];
    disabled = false;
    loading = false;
    showCreateForm = false;

    createForm!: FormGroup;
    currencyOptions = [
        { label: 'DOP (Peso Dominicano)', value: 'Dop' },
        { label: 'USD (Dólar Estadounidense)', value: 'Usd' },
    ];

    // Max 2 currencies allowed: 1 DOP and 1 USD
    // State properties (cached)
    selectedCurrencies: CurrencyEntryDto[] = [];
    hasDop = false;
    hasUsd = false;
    isLimitReached = false;
    canAddMore = true;
    availableCurrencyOptions: { label: string; value: string }[] = [];
    dropdownItems: any[] = [];

    updateState() {
        // Cache selected currencies
        this.selectedCurrencies = this.currencies.filter(c => this.selectedIds.includes(c.id));

        // Update flags
        this.hasDop = this.selectedCurrencies.some(c => c.currency === 'Dop');
        this.hasUsd = this.selectedCurrencies.some(c => c.currency === 'Usd');
        this.isLimitReached = this.hasDop && this.hasUsd;
        this.canAddMore = this.selectedIds.length < 2 && !this.isLimitReached;

        // Update available options for create form
        this.availableCurrencyOptions = this.currencyOptions.filter(opt => {
            if (opt.value === 'Dop' && this.hasDop) return false;
            if (opt.value === 'Usd' && this.hasUsd) return false;
            return true;
        });

        // Update dropdown items with disabled state
        this.dropdownItems = this.currencies.map(c => ({
            ...c,
            disabled: !this.canSelectCurrency(c)
        }));
    }

    private onChange: (value: number[]) => void = () => { };
    private onTouched: () => void = () => { };

    constructor(
        private currenciesService: CurrenciesService,
        private fb: FormBuilder,
    ) { }

    async ngOnInit() {
        this.createForm = this.fb.group({
            currency: ['Dop', Validators.required],
            min: [0, [Validators.required, Validators.min(0)]],
            max: [0, [Validators.required, Validators.min(0)]],
        });
        await this.loadCurrencies();
    }

    async loadCurrencies() {
        this.loading = true;
        try {
            this.currencies = await lastValueFrom(this.currenciesService.getAll());
            this.updateState();
        } catch (e) {
            console.error('Error loading currencies', e);
        } finally {
            this.loading = false;
        }
    }

    writeValue(value: number[] | null): void {
        this.selectedIds = value ?? [];
        this.updateState();
    }

    registerOnChange(fn: (value: number[]) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }

    onSelectionChange(ids: number[]): void {
        const addedId = ids.find(id => !this.selectedIds.includes(id));

        if (addedId) {
            const addedCurrency = this.currencies.find(c => c.id === addedId);
            if (addedCurrency) {
                // Find if there is already a currency with the same code
                const existingSameCoin = this.currencies.find(c =>
                    c.currency === addedCurrency.currency &&
                    this.selectedIds.includes(c.id) &&
                    c.id !== addedId
                );

                if (existingSameCoin) {
                    // Replace existing one with the new one
                    ids = ids.filter(id => id !== existingSameCoin.id);
                }
            }
        }

        this.selectedIds = ids;
        this.onChange(this.selectedIds);
        this.onTouched();
        this.updateState();
    }

    canSelectCurrency(currency: CurrencyEntryDto): boolean {
        // We now allow selecting any currency, and the replacement logic handles the limit
        return true;
    }

    toggleCreateForm(): void {
        this.showCreateForm = !this.showCreateForm;
        if (this.showCreateForm) {
            this.createForm.reset({ currency: 'Dop', min: 0, max: 0 });
        }
    }

    async saveNewCurrency() {
        if (this.createForm.invalid) return;

        this.loading = true;
        try {
            const newId = await lastValueFrom(
                this.currenciesService.create(this.createForm.value),
            );
            await this.loadCurrencies();
            this.selectedIds = [...this.selectedIds, newId];
            this.updateState();
            this.onChange(this.selectedIds);
            this.showCreateForm = false;
        } catch (e) {
            console.error('Error creating currency', e);
        } finally {
            this.loading = false;
        }
    }


}
